import { fetchWithRetry } from './ApiHelper';

const BASE_PATH = `${process.env.REACT_APP_BACKEND_ADDRESS || ""}/api/race/game-sessions`;

export class GameSessionApiError extends Error {
    constructor(status, message) {
        super(message || `Gameplay session request failed (${status})`);
        this.name = 'GameSessionApiError';
        this.status = status;
    }
}

export const QUALIFIER_DESKTOP_MESSAGE = 'This Grand Prix includes qualifying heats. Resume it in the desktop app.';

export function assertWebCompatibleDraft(draftState) {
    if (draftState?.tieBreak?.bracket !== undefined) {
        throw new GameSessionApiError(400, QUALIFIER_DESKTOP_MESSAGE);
    }
}

async function request(path, options = {}) {
    const response = await fetchWithRetry(`${BASE_PATH}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    if (!response.ok) {
        let message;
        try {
            const body = await response.json();
            message = body.detail || body.message;
        } catch (_) {
            // The status is enough when an upstream returns a non-JSON error.
        }
        throw new GameSessionApiError(response.status, message);
    }
    return response.json();
}

export function createGameSession(draftState) {
    assertWebCompatibleDraft(draftState);
    return request('', {
        method: 'POST',
        body: JSON.stringify({ draftState }),
    });
}

export function getActiveGameSession() {
    return request('/active');
}

export function getGameSession(sessionId) {
    return request(`/${encodeURIComponent(sessionId)}`);
}

export function updateGameSession(sessionId, expectedVersion, draftState) {
    assertWebCompatibleDraft(draftState);
    return request(`/${encodeURIComponent(sessionId)}`, {
        method: 'PUT',
        body: JSON.stringify({ expectedVersion, draftState }),
    });
}

export function completeGameSession(sessionId, expectedVersion, draftState) {
    assertWebCompatibleDraft(draftState);
    return request(`/${encodeURIComponent(sessionId)}/complete`, {
        method: 'POST',
        body: JSON.stringify({
            expectedVersion,
            grandPrix: {
                title: draftState.setup.title,
                ruleId: draftState.setup.ruleId,
                hardcore: draftState.setup.hardcore,
            },
            races: draftState.races,
        }),
    });
}

export async function abandonGameSession(sessionId, expectedVersion, draftState) {
    const persistedDraft = draftState === undefined ? (await getGameSession(sessionId)).draftState : draftState;
    assertWebCompatibleDraft(persistedDraft);
    return request(`/${encodeURIComponent(sessionId)}/abandon`, {
        method: 'POST',
        body: JSON.stringify({ expectedVersion }),
    });
}
